import { useCallback, useEffect, useRef, useState } from 'react';
import { AlertCircle, Download, Loader2, RefreshCw } from 'lucide-react';
import { Document, Page } from '@/Components/shared/react-pdf-lazy';
import { PdfViewerModal } from '@/Components/shared/PdfViewerModal';
import { usePresaleSheetPdf } from '../hooks/usePresaleSheetPdf';
import { usePresaleSheetConfig } from '../hooks/usePresaleSheetConfig';
import type { PresaleSheetConfig } from '../api/presaleSheetApi';
import { DEFAULT_HEADER_IMAGE } from '../constants';

interface PresaleSheetPreviewProps {
  warrantyUuid: string;
  warrantyName: string | null;
  initialConfig: PresaleSheetConfig;
  showDownloadButton?: boolean;
}

/** Ratio d'une page A4 (210 / 297). Sert a reserver la hauteur du conteneur
 *  des le 1er paint, pour que le ResizeObserver mesure la vraie largeur au
 *  lieu de celle d'un conteneur reduit au spinner. */
const RATIO_A4 = '1 / 1.414';

/**
 * Affiche la prévisualisation du PDF de la brochure avant-vente.
 * Lit la config courante via React Query (meme cle de cache que l'editeur)
 * pour rester synchronisee a chaque mutation upsert/upload/delete.
 */
export function PresaleSheetPreview({
  warrantyUuid,
  warrantyName,
  initialConfig,
  showDownloadButton = true,
}: PresaleSheetPreviewProps) {
  // L'editeur et la preview partagent le meme cache : des qu'une mutation
  // (auto-save, upload, delete) met a jour la config, ce composant
  // re-render avec les nouvelles valeurs sans aucun useEffect de sync.
  const { data: liveConfig } = usePresaleSheetConfig(warrantyUuid);
  const currentConfig = liveConfig ?? initialConfig;

  const headerImageForPdf = currentConfig.headerImageUrl ?? DEFAULT_HEADER_IMAGE;

  // Debounce local du bannerColor : la query React Query fait partie de la
  // queryKey, donc tout changement re-fetch le PDF. On debounce 1.5s pour
  // ne pas spammer le generateur PDF pendant le drag du color picker.
  const [debouncedBannerColor, setDebouncedBannerColor] = useState(currentConfig.bannerColor);
  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedBannerColor(currentConfig.bannerColor), 1500);
    return () => clearTimeout(timeout);
  }, [currentConfig.bannerColor]);

  const {
    data: pdfBlob,
    isLoading: pdfLoading,
    error: pdfError,
    refetch: refetchPdf,
  } = usePresaleSheetPdf(
    warrantyUuid,
    currentConfig.showPrice,
    debouncedBannerColor,
    headerImageForPdf,
  );

  // Le Blob est passe tel quel a <Document> : react-pdf sait le lire
  // directement (branche `isBlob` -> `loadFromFile`).
  //
  // NE PAS repasser par `URL.createObjectURL` ici. L'URL etait creee au render
  // et revoquee dans un cleanup d'effet ; or StrictMode rejoue effet -> cleanup
  // -> effet a chaque montage, donc le cleanup revoquait l'URL juste apres le
  // mount, sans que le memo soit rejoue. pdf.js recevait alors une URL morte
  // (ERR_FILE_NOT_FOUND, « Unexpected server response (0) ») et <Document>
  // affichait son erreur par defaut a la place du spinner.

  const [containerWidth, setContainerWidth] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Ref callback plutot qu'un `useEffect([])` : le conteneur n'est monte
  // qu'une fois le PDF recu, donc au moment ou un `useEffect([])` s'executait
  // il n'existait pas encore et l'observer n'etait jamais attache. La largeur
  // restait a 0, <Page> se rendait a sa taille naturelle (793 px) dans un
  // conteneur deux fois plus etroit et en `overflow: hidden` : la preview
  // apparaissait rognee.
  //
  // Une ref callback est appelee au moment ou le noeud entre dans le DOM,
  // avant le premier paint : la mesure synchrone donne la bonne largeur des
  // le premier rendu, et l'observer suit les redimensionnements ensuite.
  const observerRef = useRef<ResizeObserver | null>(null);
  const mesurerConteneur = useCallback((element: HTMLDivElement | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;
    if (!element) return;
    setContainerWidth(element.getBoundingClientRect().width);
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });
    observer.observe(element);
    observerRef.current = observer;
  }, []);

  const handleDownload = () => {
    if (!pdfBlob) return;
    const url = URL.createObjectURL(pdfBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `brochure-${warrantyName || 'avant-vente'}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Meme bloc d'erreur pour l'echec de generation (API) et l'echec de lecture
  // (pdf.js) : pour le vendeur c'est la meme chose, la brochure ne s'affiche pas.
  const blocErreur = (
    <div className="tw-flex tw-flex-col sm:tw-flex-row sm:tw-items-center tw-gap-3 tw-py-8 tw-px-6 tw-bg-red-50 tw-rounded-xl tw-border tw-border-red-200">
      <div className="tw-flex tw-items-center tw-gap-3 tw-flex-1">
        <AlertCircle className="tw-w-5 tw-h-5 tw-text-red-500 tw-shrink-0" />
        <span className="tw-text-sm tw-text-red-600">
          Impossible d&apos;afficher la brochure pour le moment.
        </span>
      </div>
      <button
        type="button"
        onClick={() => refetchPdf()}
        className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-px-3 tw-py-1.5 tw-bg-white tw-text-red-600 tw-border tw-border-red-200 tw-rounded-lg tw-text-sm tw-font-medium hover:tw-bg-red-100 tw-transition-colors"
      >
        <RefreshCw className="tw-w-4 tw-h-4" />
        Réessayer
      </button>
    </div>
  );

  return (
    <>
      {pdfLoading && (
        <div className="tw-flex tw-items-center tw-justify-center tw-py-20 tw-bg-gray-50 tw-rounded-xl tw-border tw-border-gray-200">
          <Loader2 className="tw-w-8 tw-h-8 tw-animate-spin tw-text-affinicar-primary" />
          <span className="tw-ml-3 tw-text-sm tw-text-gray-500">Génération de la brochure...</span>
        </div>
      )}

      {pdfError && !pdfLoading && blocErreur}

      {pdfBlob && !pdfLoading && (
        <>
          <div
            ref={mesurerConteneur}
            onClick={() => setIsModalOpen(true)}
            className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-200 tw-shadow-sm tw-overflow-hidden tw-cursor-pointer hover:tw-shadow-md tw-transition-shadow"
          >
            <Document
              file={pdfBlob}
              loading={
                <div
                  className="tw-flex tw-items-center tw-justify-center tw-w-full"
                  style={{ aspectRatio: RATIO_A4 }}
                >
                  <Loader2 className="tw-w-6 tw-h-6 tw-animate-spin tw-text-gray-400" />
                </div>
              }
              error={blocErreur}
            >
              <Page
                pageNumber={1}
                width={containerWidth > 0 ? containerWidth : undefined}
                renderTextLayer={false}
                renderAnnotationLayer={false}
              />
            </Document>
          </div>

          <PdfViewerModal
            pdfBlob={pdfBlob || null}
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
          />

          {showDownloadButton && (
            <div className="tw-mt-4">
              <button
                onClick={handleDownload}
                className="tw-w-full sm:tw-w-auto tw-flex tw-items-center tw-justify-center tw-gap-2 tw-px-4 tw-py-2 tw-bg-affinicar-primary tw-text-white tw-rounded-lg tw-text-sm tw-font-medium hover:tw-bg-affinicar-dark tw-transition-colors"
              >
                <Download className="tw-w-4 tw-h-4" />
                Télécharger
              </button>
            </div>
          )}
        </>
      )}
    </>
  );
}
